import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import LoginPage from "@/pages/LoginPage";
import TenantPortal from "@/pages/TenantPortal";
import ConsolidatedReportPage from "@/pages/ConsolidatedReportPage";
import QuickLogPage from "@/pages/QuickLogPage";
import NotificationsPage from "@/pages/NotificationsPage";
import HomePage from "@/pages/HomePage";
import ReportHeaderPage from "@/pages/ReportHeaderPage";
import DashboardPage from "@/pages/DashboardPage";
import EquipmentHoursPage from "@/pages/EquipmentHoursPage";
import MaterialMovementsPage from "@/pages/MaterialMovementsPage";
import DelaysPage from "@/pages/DelaysPage";
import SafetyQualityPage from "@/pages/SafetyQualityPage";
import ReviewSubmitPage from "@/pages/ReviewSubmitPage";
import AdminPage from "@/pages/AdminPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ViewerPage from "@/pages/ViewerPage";
import { useEffect } from "react";

// Hook to get the tenant prefix for navigation
function useTenantPrefix() {
  const storedSlug = sessionStorage.getItem("tenant_slug");
  return storedSlug ? `/${storedSlug}` : "";
}

function ProtectedRoute({ children, adminOnly, adminOrViewer }) {
  const { user } = useAuth();
  const prefix = useTenantPrefix();
  if (!user) return <Navigate to={`${prefix}/login`} replace />;
  if (adminOnly && !["admin", "tenant_admin"].includes(user.role)) return <Navigate to={`${prefix}/home`} replace />;
  if (adminOrViewer && !["admin", "tenant_admin", "viewer"].includes(user.role)) return <Navigate to={`${prefix}/home`} replace />;
  return children;
}

function getHomePath(role, prefix = "") {
  if (role === "admin" || role === "tenant_admin") return `${prefix}/admin`;
  if (role === "viewer") return `${prefix}/reports`;
  return `${prefix}/home`;
}

// Wrapper to sync URL tenant slug with sessionStorage
function TenantRouteWrapper({ children }) {
  const { slug } = useParams();
  
  useEffect(() => {
    if (slug) {
      sessionStorage.setItem("tenant_slug", slug);
    }
  }, [slug]);
  
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const prefix = useTenantPrefix();
  
  return (
    <Routes>
      {/* Default routes (no tenant prefix) */}
      <Route path="/login" element={user ? <Navigate to={getHomePath(user.role, prefix)} replace /> : <LoginPage />} />
      <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/report/:reportId/header" element={<ProtectedRoute><ReportHeaderPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/quick-log" element={<ProtectedRoute><QuickLogPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/equipment-hours" element={<ProtectedRoute><EquipmentHoursPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/material-movements" element={<ProtectedRoute><MaterialMovementsPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/delays" element={<ProtectedRoute><DelaysPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/safety-quality" element={<ProtectedRoute><SafetyQualityPage /></ProtectedRoute>} />
      <Route path="/report/:reportId/review" element={<ProtectedRoute><ReviewSubmitPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute adminOrViewer><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute adminOrViewer><ViewerPage /></ProtectedRoute>} />
      <Route path="/consolidated" element={<ProtectedRoute adminOnly><ConsolidatedReportPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute adminOrViewer><NotificationsPage /></ProtectedRoute>} />
      
      {/* Tenant-prefixed routes (e.g., /byk/login, /byk/home) */}
      <Route path="/:slug/login" element={<TenantRouteWrapper>{user ? <Navigate to={getHomePath(user.role, prefix)} replace /> : <LoginPage />}</TenantRouteWrapper>} />
      <Route path="/:slug/home" element={<TenantRouteWrapper><ProtectedRoute><HomePage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/header" element={<TenantRouteWrapper><ProtectedRoute><ReportHeaderPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/dashboard" element={<TenantRouteWrapper><ProtectedRoute><DashboardPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/quick-log" element={<TenantRouteWrapper><ProtectedRoute><QuickLogPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/equipment-hours" element={<TenantRouteWrapper><ProtectedRoute><EquipmentHoursPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/material-movements" element={<TenantRouteWrapper><ProtectedRoute><MaterialMovementsPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/delays" element={<TenantRouteWrapper><ProtectedRoute><DelaysPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/safety-quality" element={<TenantRouteWrapper><ProtectedRoute><SafetyQualityPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/report/:reportId/review" element={<TenantRouteWrapper><ProtectedRoute><ReviewSubmitPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/admin" element={<TenantRouteWrapper><ProtectedRoute adminOnly><AdminPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/analytics" element={<TenantRouteWrapper><ProtectedRoute adminOrViewer><AnalyticsPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/reports" element={<TenantRouteWrapper><ProtectedRoute adminOrViewer><ViewerPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/consolidated" element={<TenantRouteWrapper><ProtectedRoute adminOnly><ConsolidatedReportPage /></ProtectedRoute></TenantRouteWrapper>} />
      <Route path="/:slug/notifications" element={<TenantRouteWrapper><ProtectedRoute adminOrViewer><NotificationsPage /></ProtectedRoute></TenantRouteWrapper>} />
      
      {/* Short URL entry point (e.g., /byk) - validates tenant and redirects */}
      <Route path="/:slug" element={<TenantPortal />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to={user ? getHomePath(user.role, prefix) : `${prefix}/login`} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <TenantProvider>
        <AuthProvider>
          <OfflineProvider>
            <BrowserRouter>
              <div className="min-h-screen bg-background">
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </div>
              <OfflineBanner />
              <Toaster
                theme="dark"
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#1B2430',
                    border: '1px solid hsl(215 20% 20%)',
                    color: '#FFFFFF',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  },
                }}
              />
            </BrowserRouter>
          </OfflineProvider>
        </AuthProvider>
      </TenantProvider>
    </ErrorBoundary>
  );
}

export default App;
